import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trash, PencilSimple, Eye, Copy, ArrowsMerge, CheckCircle, Warning, Megaphone } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminTeamPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', license_number: '', signature_short: '', professional_title: '' });
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/team`); setMembers(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    try {
      if (editId) { await axios.put(`${API}/api/team/${editId}`, form); }
      else { await axios.post(`${API}/api/team`, form); }
      toast.success(editId ? 'Membro atualizado' : 'Membro criado');
      setShowForm(false); setEditId(null); setForm({ name: '', email: '', password: '', phone: '', license_number: '', signature_short: '', professional_title: '' }); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const del = async (id) => {
    const m = members.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o membro da equipa "${m?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/team/${id}`); toast.success('Eliminado'); fetch();
  };

  return (
    <div className="fade-in" data-testid="admin-team-page">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="font-heading font-medium text-2xl text-brand-text">Equipa</h2><p className="text-sm text-brand-muted mt-1">Gestão de nutricionistas</p></div>
        <button onClick={() => { setEditId(null); setForm({ name: '', email: '', password: '', phone: '', license_number: '', signature_short: '', professional_title: '' }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={16} /> Novo membro</button>
      </div>

      {loading ? <p className="text-center py-8 text-brand-muted">A carregar...</p> : members.length === 0 ? (
        <div className="text-center py-16"><Users size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" /><p className="text-brand-muted">Sem membros na equipa.</p></div>
      ) : (
        <div className="space-y-3">
          {members.map(m => (
            <div key={m.user_id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-mint/20 flex items-center justify-center flex-shrink-0"><span className="font-heading font-medium text-brand-green">{(m.name || '?')[0]}</span></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-brand-text">{m.name}</p>
                <p className="text-[10px] text-brand-muted">{m.email}{m.license_number && ` · Cédula ${m.license_number}`}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditId(m.user_id); setForm({ name: m.name, email: m.email, password: '', phone: m.phone || '', license_number: m.license_number || '', signature_short: m.signature_short || '', professional_title: m.professional_title || '' }); setShowForm(true); }} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`team-edit-${m.user_id}`}><PencilSimple size={14} /></button>
                <button onClick={() => del(m.user_id)} className="p-1.5 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar membro' : 'Novo membro da equipa'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome completo</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl border-brand-border" required /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Email profissional</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-xl border-brand-border" required disabled={!!editId} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">{editId ? 'Nova palavra-passe (deixar vazio para manter)' : 'Palavra-passe'}</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Número de ordem (cédula profissional)</Label><Input value={form.license_number} onChange={e => setForm({...form, license_number: e.target.value})} placeholder="Ex: 2758N" className="rounded-xl border-brand-border" /></div>
            {editId && <div><Label className="text-xs text-brand-muted mb-1 block">Assinatura curta (emails)</Label><Input value={form.signature_short} onChange={e => setForm({...form, signature_short: e.target.value})} placeholder="Ex: Nome | Equipa nutrianapereira" className="rounded-xl border-brand-border" data-testid="team-signature-short-input" /></div>}
            {editId && <div><Label className="text-xs text-brand-muted mb-1 block">Título profissional</Label><Input value={form.professional_title} onChange={e => setForm({...form, professional_title: e.target.value})} placeholder="Ex: Nutricionista na área materno-infantil" className="rounded-xl border-brand-border" data-testid="team-professional-title-input" /></div>}
            <div><Label className="text-xs text-brand-muted mb-1 block">Telefone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-xl border-brand-border" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="team-save-btn">{editId ? 'Guardar' : 'Criar membro'}</button>
          </div>
        </DialogContent>
      </Dialog>

      <ViewersSection />

      <ColaboradoresSection />

      <MarketingTeamSection />

      <StaffDuplicatesSection />
    </div>
  );
}

function ViewersSection() {
  const confirmDelete = useConfirmDelete();
  const [viewers, setViewers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(true);

  const fetchViewers = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/viewers`); setViewers(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchViewers(); }, [fetchViewers]);

  const save = async () => {
    try {
      if (editId) await axios.put(`${API}/api/viewers/${editId}`, { name: form.name, email: form.email, ...(form.password ? { password: form.password } : {}) });
      else await axios.post(`${API}/api/viewers`, form);
      toast.success(editId ? 'Viewer atualizado' : 'Viewer criado');
      setShowForm(false); setEditId(null); setForm({ name: '', email: '', password: '' }); fetchViewers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const toggleStatus = async (v) => { try { await axios.put(`${API}/api/viewers/${v.user_id}`, { status: v.status === 'ativo' ? 'inativo' : 'ativo' }); fetchViewers(); } catch { toast.error('Erro'); } };
  const del = async (id) => {
    const v = viewers.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar permanentemente o viewer "${v?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/viewers/${id}`); toast.success('Viewer eliminado'); fetchViewers();
  };
  const fmtDate = (d) => d ? fmtStamp(d) : 'Nunca';

  return (
    <div className="mt-10" data-testid="viewers-section">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="font-heading font-medium text-lg text-brand-text">Viewers</h3><p className="text-xs text-brand-muted mt-0.5">Acesso só de leitura a métricas e finanças agregadas (mentor de negócio / secretária)</p></div>
        <button onClick={() => { setEditId(null); setForm({ name: '', email: '', password: '' }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-2" data-testid="new-viewer-btn"><Plus size={14} /> Novo viewer</button>
      </div>

      {loading ? <p className="text-center py-6 text-brand-muted text-sm">A carregar...</p> : viewers.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-brand-border rounded-2xl"><Eye size={36} className="mx-auto text-brand-placeholder mb-2" weight="duotone" /><p className="text-brand-muted text-sm">Sem viewers criados.</p></div>
      ) : (
        <div className="space-y-3">
          {viewers.map(v => (
            <div key={v.user_id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4" data-testid={`viewer-row-${v.user_id}`}>
              <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0"><Eye size={18} className="text-brand-green" weight="duotone" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-brand-text">{v.name}</p>
                <p className="text-[10px] text-brand-muted">{v.email} · Último acesso: {fmtDate(v.last_login)}</p>
              </div>
              <button onClick={() => toggleStatus(v)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${v.status === 'ativo' ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`viewer-toggle-${v.user_id}`}>{v.status === 'ativo' ? 'Ativo' : 'Inativo'}</button>
              <div className="flex gap-1">
                <button onClick={() => { setEditId(v.user_id); setForm({ name: v.name, email: v.email, password: '' }); setShowForm(true); }} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`viewer-edit-${v.user_id}`}><PencilSimple size={14} /></button>
                <button onClick={() => del(v.user_id)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`viewer-delete-${v.user_id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar viewer' : 'Novo viewer'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome completo</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" data-testid="viewer-name-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl border-brand-border" data-testid="viewer-email-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">{editId ? 'Nova palavra-passe (deixar vazio para manter)' : 'Palavra-passe'}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-xl border-brand-border" data-testid="viewer-password-input" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="viewer-save-btn">{editId ? 'Guardar' : 'Criar viewer'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ColaboradoresSection() {
  const confirmDelete = useConfirmDelete();
  const [collabs, setCollabs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(true);

  const fetchCollabs = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/collaborators`); setCollabs(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchCollabs(); }, [fetchCollabs]);

  const save = async () => {
    try {
      if (editId) await axios.put(`${API}/api/collaborators/${editId}`, { name: form.name, email: form.email, phone: form.phone, ...(form.password ? { password: form.password } : {}) });
      else await axios.post(`${API}/api/collaborators`, form);
      toast.success(editId ? 'Colaborador atualizado' : 'Colaborador criado');
      setShowForm(false); setEditId(null); setForm({ name: '', email: '', password: '', phone: '' }); fetchCollabs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const toggleStatus = async (c) => { try { await axios.put(`${API}/api/collaborators/${c.user_id}`, { status: c.status === 'ativo' ? 'inativo' : 'ativo' }); fetchCollabs(); } catch { toast.error('Erro'); } };
  const del = async (id) => {
    const c = collabs.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar permanentemente o colaborador "${c?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/collaborators/${id}`); toast.success('Colaborador eliminado'); fetchCollabs();
  };

  return (
    <div className="mt-10" data-testid="colaboradores-section">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="font-heading font-medium text-lg text-brand-text">Colaboradores</h3><p className="text-xs text-brand-muted mt-0.5">Staff sem acesso clínico (ex.: Carlos, Carolina), só usam o módulo de Tarefas e recebem lembretes por WhatsApp</p></div>
        <button onClick={() => { setEditId(null); setForm({ name: '', email: '', password: '', phone: '' }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-2" data-testid="new-collaborator-btn"><Plus size={14} /> Novo colaborador</button>
      </div>

      {loading ? <p className="text-center py-6 text-brand-muted text-sm">A carregar...</p> : collabs.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-brand-border rounded-2xl"><Users size={36} className="mx-auto text-brand-placeholder mb-2" weight="duotone" /><p className="text-brand-muted text-sm">Sem colaboradores criados.</p></div>
      ) : (
        <div className="space-y-3">
          {collabs.map(c => (
            <div key={c.user_id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4" data-testid={`collaborator-row-${c.user_id}`}>
              <div className="w-10 h-10 rounded-full bg-brand-gold/15 flex items-center justify-center flex-shrink-0"><span className="font-heading font-medium text-brand-gold-hover">{(c.name || '?')[0]}</span></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-brand-text">{c.name}</p>
                <p className="text-[10px] text-brand-muted">{c.email}{c.phone && ` · ${c.phone}`}</p>
              </div>
              <button onClick={() => toggleStatus(c)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${c.status === 'ativo' ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`collaborator-toggle-${c.user_id}`}>{c.status === 'ativo' ? 'Ativo' : 'Inativo'}</button>
              <div className="flex gap-1">
                <button onClick={() => { setEditId(c.user_id); setForm({ name: c.name, email: c.email, password: '', phone: c.phone || '' }); setShowForm(true); }} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`collaborator-edit-${c.user_id}`}><PencilSimple size={14} /></button>
                <button onClick={() => del(c.user_id)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`collaborator-delete-${c.user_id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar colaborador' : 'Novo colaborador'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome completo</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" data-testid="collaborator-name-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl border-brand-border" data-testid="collaborator-email-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Telemóvel <span className="text-[10px] text-brand-placeholder">(para lembretes de tarefas via WhatsApp)</span></Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="9XX XXX XXX" className="rounded-xl border-brand-border" data-testid="collaborator-phone-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">{editId ? 'Nova palavra-passe (deixar vazio para manter)' : 'Palavra-passe'}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-xl border-brand-border" data-testid="collaborator-password-input" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="collaborator-save-btn">{editId ? 'Guardar' : 'Criar colaborador'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function MarketingTeamSection() {
  const confirmDelete = useConfirmDelete();
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'marketing_editor' });
  const [loading, setLoading] = useState(true);

  const fetchMembers = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/marketing/team`); setMembers(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const save = async () => {
    try {
      if (editId) await axios.put(`${API}/api/marketing/team/${editId}`, { name: form.name, email: form.email, role: form.role, ...(form.password ? { password: form.password } : {}) });
      else await axios.post(`${API}/api/marketing/team`, form);
      toast.success(editId ? 'Membro atualizado' : 'Membro criado');
      setShowForm(false); setEditId(null); setForm({ name: '', email: '', password: '', role: 'marketing_editor' }); fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const toggleStatus = async (m) => { try { await axios.put(`${API}/api/marketing/team/${m.user_id}`, { status: m.status === 'ativo' ? 'inativo' : 'ativo' }); fetchMembers(); } catch { toast.error('Erro'); } };
  const del = async (id) => {
    const m = members.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar permanentemente o membro da equipa de marketing "${m?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/marketing/team/${id}`); toast.success('Eliminado'); fetchMembers();
  };
  const roleLabel = (r) => r === 'marketing_editor' ? 'Editor' : 'Viewer';

  return (
    <div className="mt-10" data-testid="marketing-team-section">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="font-heading font-medium text-lg text-brand-text">Equipa de Marketing</h3><p className="text-xs text-brand-muted mt-0.5">Acesso externo isolado ao Calendário Editorial (ex.: social media manager), Editor cria/edita, Viewer só vê</p></div>
        <button onClick={() => { setEditId(null); setForm({ name: '', email: '', password: '', role: 'marketing_editor' }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-2" data-testid="new-marketing-member-btn"><Plus size={14} /> Novo membro</button>
      </div>

      {loading ? <p className="text-center py-6 text-brand-muted text-sm">A carregar...</p> : members.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-brand-border rounded-2xl"><Megaphone size={36} className="mx-auto text-brand-placeholder mb-2" weight="duotone" /><p className="text-brand-muted text-sm">Sem membros da equipa de marketing.</p></div>
      ) : (
        <div className="space-y-3">
          {members.map(m => (
            <div key={m.user_id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4" data-testid={`marketing-member-row-${m.user_id}`}>
              <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0"><Megaphone size={18} className="text-brand-green" weight="duotone" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-brand-text">{m.name}</p>
                <p className="text-[10px] text-brand-muted">{m.email}</p>
              </div>
              <span className="text-[10px] px-2.5 py-1 rounded-full font-medium bg-brand-bg border border-brand-border text-brand-muted">{roleLabel(m.role)}</span>
              <button onClick={() => toggleStatus(m)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${m.status === 'ativo' ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`marketing-member-toggle-${m.user_id}`}>{m.status === 'ativo' ? 'Ativo' : 'Inativo'}</button>
              <div className="flex gap-1">
                <button onClick={() => { setEditId(m.user_id); setForm({ name: m.name, email: m.email, password: '', role: m.role }); setShowForm(true); }} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`marketing-member-edit-${m.user_id}`}><PencilSimple size={14} /></button>
                <button onClick={() => del(m.user_id)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`marketing-member-delete-${m.user_id}`}><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar membro' : 'Novo membro da equipa de marketing'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome completo</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" data-testid="marketing-member-name-input" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl border-brand-border" data-testid="marketing-member-email-input" /></div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Papel</Label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="marketing-member-role-input">
                <option value="marketing_editor">Editor (cria/edita conteúdos)</option>
                <option value="marketing_viewer">Viewer (só consulta)</option>
              </select>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">{editId ? 'Nova palavra-passe (deixar vazio para manter)' : 'Palavra-passe'}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-xl border-brand-border" data-testid="marketing-member-password-input" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="marketing-member-save-btn">{editId ? 'Guardar' : 'Criar membro'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StaffDuplicatesSection() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState('');

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/staff-duplicates`);
      setGroups(data.duplicate_groups || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao analisar'); }
    finally { setLoading(false); }
  }, []);

  const merge = async (oldId, newId, name) => {
    if (!window.confirm(`Fundir o registo duplicado de "${name}" no registo canónico?\n\nTodas as referências passam para o canónico e o duplicado fica inativo (não é apagado).`)) return;
    setMerging(oldId);
    try {
      const { data } = await axios.post(`${API}/api/admin/staff-duplicates/merge`, { old_user_id: oldId, new_user_id: newId });
      toast.success(`Fundido. ${data.remaining_refs === 0 ? 'Zero referências órfãs.' : `${data.remaining_refs} referências por resolver.`}`);
      await scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao fundir'); }
    finally { setMerging(''); }
  };

  return (
    <div className="mt-10" data-testid="staff-duplicates-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-medium text-lg text-brand-text flex items-center gap-2"><Copy size={18} weight="duotone" className="text-brand-green" /> Duplicados de equipa</h3>
          <p className="text-xs text-brand-muted mt-0.5">Deteta registos da mesma pessoa (nome igual) e funde-os num único, sem apagar dados.</p>
        </div>
        <button onClick={scan} disabled={loading} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-2 disabled:opacity-60" data-testid="scan-duplicates-btn">
          {loading ? 'A analisar…' : 'Analisar duplicados'}
        </button>
      </div>

      {groups !== null && groups.length === 0 && (
        <div className="text-center py-10 bg-white border border-dashed border-brand-border rounded-2xl" data-testid="no-duplicates">
          <CheckCircle size={36} className="mx-auto text-brand-green mb-2" weight="duotone" />
          <p className="text-brand-muted text-sm">Nenhum duplicado detetado.</p>
        </div>
      )}

      {groups !== null && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map(g => (
            <div key={g.canonical_user_id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`dup-group-${g.canonical_user_id}`}>
              <p className="text-sm font-medium text-brand-text mb-3 flex items-center gap-2"><Warning size={15} className="text-amber-500" weight="duotone" /> {g.members.length} registos com o mesmo nome</p>
              <div className="space-y-2">
                {g.members.map(m => (
                  <div key={m.user_id} className="flex items-center gap-3 border border-brand-border rounded-xl p-3" data-testid={`dup-member-${m.user_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-brand-text flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{m.name}</span>
                        {m.is_canonical && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green font-medium">Canónico</span>}
                        {m.status === 'inativo' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-bg border border-brand-border text-brand-muted">Inativo</span>}
                        {m.merged_into && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-bg border border-brand-border text-brand-muted">Já fundido</span>}
                      </p>
                      <p className="text-[10px] text-brand-muted truncate">{m.email} · {m.user_id} · <strong>{m.ref_total}</strong> referências</p>
                    </div>
                    {!m.is_canonical && !m.merged_into && (
                      <button onClick={() => merge(m.user_id, g.canonical_user_id, m.name)} disabled={merging === m.user_id} className="flex-shrink-0 bg-brand-green text-white rounded-full px-3 py-1.5 text-[11px] font-medium flex items-center gap-1.5 disabled:opacity-60" data-testid={`merge-btn-${m.user_id}`}>
                        <ArrowsMerge size={13} /> {merging === m.user_id ? 'A fundir…' : 'Fundir no canónico'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
